import React from "react";
import { Calendar, Award, Users, TrendingUp } from "lucide-react";
import { Button } from "./ui/button";
import { doctorInfo } from "../mockData";

const Hero = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="hero"
      className="pt-20 bg-gradient-to-br from-cyan-50 via-blue-50 to-purple-50 relative overflow-hidden"
    >
      {/* Decorative elements */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-gradient-to-br from-cyan-200/30 to-blue-300/30 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-gradient-to-tr from-purple-200/30 to-pink-300/30 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-6">
            <div className="inline-block animate-pulse">
              <span className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
                🏆 MD, Transplant Pulmonologist
              </span>
            </div>

            <h1 className="text-6xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-purple-900 bg-clip-text text-transparent leading-tight">
              {doctorInfo.name}
            </h1>

            <p className="text-xl font-medium bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
              {doctorInfo.designation} • {doctorInfo.specialty}
            </p>

            <p className="text-lg text-gray-700 leading-relaxed">
              {doctorInfo.tagline}
            </p>

            <div className="flex flex-wrap gap-4 pt-4">
              <Button
                onClick={() => scrollToSection("appointment")}
                size="lg"
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                <Calendar className="mr-2 h-5 w-5" />
                Book Appointment
              </Button>
              <Button
                onClick={() => scrollToSection("about")}
                size="lg"
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                Learn More
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 pt-8">
              <div className="bg-gradient-to-br from-cyan-500 to-blue-500 p-6 rounded-2xl shadow-xl transform hover:scale-105 transition-transform duration-300">
                <div className="text-4xl font-bold text-white">
                  {doctorInfo.yearsOfExperience}+
                </div>
                <div className="text-sm text-cyan-50 mt-1">
                  Years Experience
                </div>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-pink-500 p-6 rounded-2xl shadow-xl transform hover:scale-105 transition-transform duration-300">
                <div className="text-4xl font-bold text-white">
                  {doctorInfo.patientsServed}
                </div>
                <div className="text-sm text-purple-50 mt-1">
                  Patients Served
                </div>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-indigo-500 p-6 rounded-2xl shadow-xl transform hover:scale-105 transition-transform duration-300">
                <div className="text-4xl font-bold text-white">
                  {doctorInfo.successRate}
                </div>
                <div className="text-sm text-blue-50 mt-1">Success Rate</div>
              </div>
            </div>
          </div>

          {/* Right Content - Doctor Image */}
          <div className="relative">
            <div className="relative overflow-hidden rounded-3xl shadow-2xl transform hover:scale-105 transition-transform duration-500">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/20 to-purple-400/20 z-10"></div>
              <img
                src={doctorInfo.heroImage}
                alt={doctorInfo.name}
                className="w-full h-[600px] object-cover"
              />
              {/* Overlay Card */}
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-2xl z-20 border-2 border-cyan-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="bg-gradient-to-br from-cyan-500 to-blue-500 p-4 rounded-xl shadow-lg">
                      <Award className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 font-medium">
                        Recognition
                      </div>
                      <div className="font-bold text-xl bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                        Top Rated Physician
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating badge */}
            <div className="absolute -top-6 -right-6 bg-gradient-to-br from-yellow-400 to-orange-500 p-6 rounded-2xl shadow-2xl transform rotate-12 hover:rotate-0 transition-transform duration-300">
              <TrendingUp className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
